import React from 'react';
import {
  FileSearch,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowRight,
  BarChart3,
  DollarSign,
} from 'lucide-react';

export interface GapSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface AnalysisResult {
  id: string;
  type: 'gap_analysis' | 'risk_analysis' | 'article7_analysis' | 'regulatory' | 'financial_risk';
  title: string;
  status: 'completed' | 'in_progress' | 'pending' | 'failed';
  completedAt?: string;
  gapSummary?: GapSummary;
  totalGaps?: number;
  totalRisks?: number;
  comparedWith?: string;
  description?: string;
}

export interface AnalysisResultsProps {
  results: AnalysisResult[];
  isLoading?: boolean;
  onViewResult?: (result: AnalysisResult) => void;
  onRunAnalysis?: () => void;
}

const analysisTypeConfig: Record<
  AnalysisResult['type'],
  { label: string; icon: React.ReactNode; color: string }
> = {
  gap_analysis: {
    label: 'Gap Analysis',
    icon: <FileSearch className="h-5 w-5" />,
    color: 'text-blue-600 bg-blue-100',
  },
  risk_analysis: {
    label: 'Risk Analysis',
    icon: <AlertTriangle className="h-5 w-5" />,
    color: 'text-orange-600 bg-orange-100',
  },
  article7_analysis: {
    label: 'Article 7 Analysis',
    icon: <BarChart3 className="h-5 w-5" />,
    color: 'text-purple-600 bg-purple-100',
  },
  regulatory: {
    label: 'Regulatory Compliance',
    icon: <CheckCircle className="h-5 w-5" />,
    color: 'text-green-600 bg-green-100',
  },
  financial_risk: {
    label: 'Financial Risk Analysis',
    icon: <DollarSign className="h-5 w-5" />,
    color: 'text-red-600 bg-red-100',
  },
};

const statusConfig: Record<
  AnalysisResult['status'],
  { label: string; color: string; bgColor: string }
> = {
  completed: { label: 'Completed', color: 'text-green-700', bgColor: 'bg-green-100' },
  in_progress: { label: 'In Progress', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  pending: { label: 'Pending', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  failed: { label: 'Failed', color: 'text-red-700', bgColor: 'bg-red-100' },
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const GapSeverityBadges: React.FC<{ summary: GapSummary }> = ({ summary }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {summary.critical > 0 && (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
        {summary.critical} Critical
      </span>
    )}
    {summary.high > 0 && (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
        {summary.high} High
      </span>
    )}
    {summary.medium > 0 && (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
        {summary.medium} Medium
      </span>
    )}
    {summary.low > 0 && (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
        {summary.low} Low
      </span>
    )}
  </div>
);

const AnalysisResultCard: React.FC<{
  result: AnalysisResult;
  onView?: (result: AnalysisResult) => void;
}> = ({ result, onView }) => {
  const typeConfig = analysisTypeConfig[result.type];
  const status = statusConfig[result.status];

  return (
    <div
      className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-sm transition-all"
      data-testid={`analysis-result-${result.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`p-2 rounded-lg ${typeConfig.color}`}>
          {typeConfig.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-medium text-gray-900 truncate">{result.title}</h4>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}
            >
              {result.status === 'in_progress' && (
                <Clock className="h-3 w-3 mr-1 animate-pulse" />
              )}
              {status.label}
            </span>
          </div>

          <p className="text-sm text-gray-500 mt-1">
            {typeConfig.label}
            {result.comparedWith && (
              <> vs {result.comparedWith}</>
            )}
          </p>

          {result.description && (
            <p className="text-sm text-gray-600 mt-2">{result.description}</p>
          )}

          {/* Gap summary */}
          {result.gapSummary && (
            <GapSeverityBadges summary={result.gapSummary} />
          )}

          {/* Stats row */}
          <div className="flex items-center gap-4 mt-3 text-sm">
            {result.totalGaps !== undefined && (
              <span className="text-gray-600">
                <strong className="text-gray-900">{result.totalGaps}</strong> gaps found
              </span>
            )}
            {result.totalRisks !== undefined && (
              <span className="text-gray-600">
                <strong className="text-gray-900">{result.totalRisks}</strong> risks identified
              </span>
            )}
          </div>

          {/* Footer with date and action */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            {result.completedAt && (
              <span className="text-xs text-gray-400">
                {formatDate(result.completedAt)}
              </span>
            )}
            {result.status === 'in_progress' && (
              <span className="text-xs text-blue-600 animate-pulse">
                Analysis in progress...
              </span>
            )}
            {onView && result.status === 'completed' && (
              <button
                onClick={() => onView(result)}
                className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                data-testid={`view-result-${result.id}`}
              >
                View Details
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultsSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    {[1, 2].map((i) => (
      <div key={i} className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/4 mb-3" />
            <div className="flex gap-2">
              <div className="h-5 bg-gray-200 rounded w-16" />
              <div className="h-5 bg-gray-200 rounded w-16" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const EmptyResults: React.FC<{ onRunAnalysis?: () => void }> = ({ onRunAnalysis }) => (
  <div className="text-center py-8" data-testid="analysis-results-empty">
    <FileSearch className="h-12 w-12 text-gray-300 mx-auto mb-3" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">No Analysis Results</h3>
    <p className="text-sm text-gray-500 mb-4">
      Run an analysis to compare this contract with others and identify gaps.
    </p>
    {onRunAnalysis && (
      <button
        onClick={onRunAnalysis}
        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        data-testid="run-first-analysis"
      >
        <FileSearch className="h-4 w-4 mr-2" />
        Run Gap Analysis
      </button>
    )}
  </div>
);

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  results,
  isLoading,
  onViewResult,
  onRunAnalysis,
}) => {
  if (isLoading) {
    return (
      <div
        className="bg-white rounded-lg shadow-md p-6"
        data-testid="analysis-results-loading"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-gray-200 rounded w-1/4" />
          <div className="h-8 bg-gray-200 rounded w-24" />
        </div>
        <ResultsSkeleton />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md" data-testid="analysis-results">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Analysis Results</h2>
          {results.length > 0 && (
            <p className="text-sm text-gray-500">
              {results.length} analysis{results.length !== 1 ? 'es' : ''} run
            </p>
          )}
        </div>
        {results.length > 0 && onRunAnalysis && (
          <button
            onClick={onRunAnalysis}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
            data-testid="run-new-analysis"
          >
            <FileSearch className="h-4 w-4 mr-1" />
            New Analysis
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {results.length === 0 ? (
          <EmptyResults onRunAnalysis={onRunAnalysis} />
        ) : (
          <div className="space-y-4">
            {results.map((result) => (
              <AnalysisResultCard
                key={result.id}
                result={result}
                onView={onViewResult}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
